import React , {useState }from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CloseIcon from "@mui/icons-material/Close";

const VacantApplyDialog = ({
  isOpen,
  onClose,
  onConfirm,
  slotData,
  shiftsList = [],
  translate = (key, fallback) => fallback || key,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!slotData) return null;

  const formattedDate =
    slotData.daySel && slotData.monthSel && slotData.yearSel
      ? `${String(slotData.daySel).padStart(2, "0")}/${String(slotData.monthSel).padStart(2, "0")}/${slotData.yearSel}`
      : "-";

  const shiftInfo = shiftsList.find((s) => s.id === slotData.anaShiftId);
  const shiftTimeText = shiftInfo
    ? `${shiftInfo.name} ${shiftInfo.timeFrom} - ${shiftInfo.timeTo}`
    : "-";

  const roleNameText = slotData.roleName || "-";

  const getTranslation = (key, fallback) => {
    const res = translate(key);
    return !res || res === key ? fallback : res;
  };

  const handleConfirm = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="vacant-apply-dialog-title"
    >
      {/* HEADER UNIFORMATO */}
      <DialogTitle
        id="vacant-apply-dialog-title"
        sx={{
          bgcolor: "primary.main",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.5,
        }}
      >
        <PersonAddAlt1Icon sx={{ color: "white" }} />
        <Typography
          variant="h6"
          component="span"
          sx={{ color: "white", fontWeight: "bold", flex: 1 }}
        >
          {getTranslation("vacantApplyTitle", "Candidatura Turno Vacante")}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENUTO */}
      <DialogContent sx={{ pt: "16px !important" }}>
        <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
          {getTranslation(
            "vacantApplyConfirmMessage",
            "Confermi di volerti candidare per coprire questo turno scoperto?"
          )}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            bgcolor: "#fafafa",
          }}
        >
          <Typography variant="body2" color="text.primary">
            <strong style={{ color: "#2c5282" }}>
              {getTranslation("dateLabel", "Data")}:
            </strong>{" "}
            {formattedDate}
          </Typography>

          <Typography variant="body2" color="text.primary">
            <strong style={{ color: "#2c5282" }}>
              {getTranslation("shiftTimeLabel", "Turno")}:
            </strong>{" "}
            {shiftTimeText}
          </Typography>

          <Typography variant="body2" color="text.primary">
            <strong style={{ color: "#2c5282" }}>
              {getTranslation("roleLabel", "Ruolo")}:
            </strong>{" "}
            {roleNameText}
          </Typography>
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block", fontStyle: "italic" }}
        >
          *{" "}
          {getTranslation(
            "vacantApplyDisclaimer",
            "La richiesta verrà inviata ai responsabili per l'approvazione finale."
          )}
        </Typography>
      </DialogContent>

      {/* FOOTER UNIFORMATO */}
      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          color="inherit" 
          fullWidth
          disabled={isLoading}
        >
          {getTranslation("cancelBtn", "Annulla")}
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="primary" 
          fullWidth
          disabled={isLoading}
        >
          {isLoading ? "Caricamento..." : getTranslation("okBtn", "OK")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

VacantApplyDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  slotData: PropTypes.object,
  shiftsList: PropTypes.array,
  translate: PropTypes.func,
};

export default VacantApplyDialog;